import { parseTemplate } from 'url-template';
import { formatApiDate } from '~/Api/DateTime';
import Query from '~/Api/Product/Query';
import mapPagination, { RawPagination } from '~/Api/Pagination/mapPagination';
import Pagination from '~/Api/Pagination/Pagination';
import UserProductGroupErrorResponse from '~/Api/User/ProductGroup/UserProductGroupErrorResponse';
import { ApiClientErrorResponse } from '~/ApiClient';
import mapUserProductGroup, { RawUserProductGroup } from './mapUserProductGroup';
import { Meta } from './Product';
import Filter from './Filter';
import UserProductGroup from './UserProductGroup';
import AuthenticatedApiClient from '~/ApiClient/AuthenticatedApiClient';

export interface Result {
	items: UserProductGroup[];
	pagination: Pagination;
}

const mapMeta = (meta: Meta): { searchFilters: { [key: string]: string | null } } => ({
	searchFilters: meta.searchFilters.reduce((result, filterParameter) => {
		if (filterParameter.name === 'newerThan') {
			return {
				...result,
				[filterParameter.name]: formatApiDate(filterParameter.parseDateValue() || new Date()),
			};
		} else if (filterParameter.value instanceof Array) {
			return {
				...result,
				[filterParameter.name]: filterParameter.value.join(','),
			};
		} else if (filterParameter.value !== '') {
			return {
				...result,
				[filterParameter.name]: filterParameter.value,
			};
		}

		return result;
	}, {}),
});

export default class UserProductGroupApi {
	constructor(private readonly authenticatedApiClient: AuthenticatedApiClient) {}

	async addProductsBySearch(groupId: string, query: Query): Promise<void> {
		const mappedFilters = query.filterParameters.reduce(
			(
				result: { countries?: string | string[]; datasources?: string | string[]; newerThan?: string | string[] } & {
					[key: string]: string | string[];
				},
				filterParameter
			) => {
				if (filterParameter.value === '') {
					return result;
				}

				if (filterParameter.name === 'country') {
					result.countries = filterParameter.value;
				} else if (filterParameter.name === 'datasource') {
					result.datasources = filterParameter.value;
				} else if (filterParameter.name === 'newerThan') {
					result[filterParameter.name] = formatApiDate(filterParameter.parseDateValue() || new Date());
				} else {
					result[filterParameter.name] = filterParameter.value;
				}

				return result;
			},
			{}
		);

		const url = parseTemplate('product-groups/{groupId}/add-products-by-search');

		try {
			await this.authenticatedApiClient.userFetch(url.expand({ groupId }), {
				method: 'POST',
				body: JSON.stringify(mappedFilters),
			});
		} catch (error) {
			throw error instanceof ApiClientErrorResponse
				? new UserProductGroupErrorResponse(error.request, error.statusCode, error.errors)
				: error;
		}
	}

	async addProductsToUserGroup(
		groupId: string,
		items: { id: string; packageSizeForSingleUnit: number | null }[],
		meta: Meta | null
	): Promise<void> {
		const url = parseTemplate('product-groups/{groupId}/products');
		const body = {
			items,
			meta: meta !== null ? mapMeta(meta) : null,
		};

		return this.authenticatedApiClient
			.userFetch(url.expand({ groupId }), {
				method: 'POST',
				body: JSON.stringify(body),
			})
			.catch((response) => ApiClientErrorResponse.wrapError(response, UserProductGroupErrorResponse));
	}

	async findUserProductGroups(filter: Filter, page: number, perPage: number): Promise<Result> {
		const url = parseTemplate('product-groups{?filter-parentId,filter-hasParent,filter-name,page,perPage}');
		return this.authenticatedApiClient
			.userFetch(
				url.expand({
					'filter-parentId': filter.parentId,
					'filter-hasParent':
						filter.parentId !== null ? '' : filter.hasParent === null ? 'any' : filter.hasParent ? 'yes' : 'no',
					'filter-name': filter.name,
					page,
					perPage,
				})
			)
			.then((response) => response.json())
			.then((response: { items: RawUserProductGroup[]; pagination: RawPagination }) => ({
				items: response.items.map((group) => mapUserProductGroup(group)),
				pagination: mapPagination(response.pagination),
			}));
	}
}
